import { publicPinnedTrophyNullableSchema } from '@crowdfpl/domain/gamification';
import { z } from 'zod';

export const DuelVoteSchema = z.object({
  id: z.string().uuid(),
  duel_id: z.string().uuid(),
  user_id: z.string().uuid(),
  selected_player_id: z.string().uuid().nullable(),
  vote_changes_count: z.number().int().nonnegative().optional(),
  voted_at: z.string().optional(),
  last_change_at: z.string().nullable().optional(),
  had_early_bonus_on_current_pick: z.boolean().optional(),
  created_at: z.string(),
  updated_at: z.string().nullable().optional(),
  // enriched user fields (optional)
  username: z.string().nullable().optional(),
  avatar_url: z.string().nullable().optional(),
  twitter_username: z.string().nullable().optional(),
  show_twitter_username: z.boolean().optional(),
  predictor_rating: z.number().int().nullable().optional(),
  success_rate: z.number().nullable().optional(),
  total_predictions: z.number().int().nullable().optional(),
  current_streak: z.number().int().nullable().optional(),
  global_rank: z.number().nullable().optional(),
  pinned_trophy: publicPinnedTrophyNullableSchema.optional(),
  // vote summary for the duel (optional, returned on vote)
  votes: z.object({
    player_a_votes: z.number().int().nonnegative(),
    player_b_votes: z.number().int().nonnegative(),
    total_votes: z.number().int().nonnegative(),
    user_vote: z.string().uuid().nullable().optional(),
  }).optional(),
  // potential_rating and rating_factors returned on vote for immediate UI update
  potential_rating: z
    .object({
      if_player_a_wins: z.number(),
      if_player_a_loses: z.number(),
      if_player_a_tie: z.number(),
      if_player_b_wins: z.number(),
      if_player_b_loses: z.number(),
      if_player_b_tie: z.number(),
      selected_player_id: z.string().uuid().nullable(),
      kPop: z.number(),
      kBase: z.number(),
      kEarly: z.number(),
      rating_multiplier: z.number().optional(),
    })
    .optional(),
  rating_factors: z
    .object({
      duel_weight: z.string(),
      duel_weight_detail: z.string(),
      early_bonus_value: z.string(),
      early_bonus_detail: z.string(),
      is_duel_of_the_week: z.boolean().optional(),
      rating_factors_debug: z
        .object({
          N: z.number(),
          Nmin: z.number(),
          Nmax: z.number(),
          kPop_formula: z.string(),
          hours_before_deadline: z.number().optional(),
          kEarly_bracket: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
});

export const DuelVotesListResponseSchema = z.object({
  duel_votes: z.array(DuelVoteSchema),
  pagination: z.object({
    limit: z.number().int().nonnegative(),
    offset: z.number().int().nonnegative(),
    total: z.number().int().nonnegative().optional(),
  }),
});

export const UniqueVoterSchema = z.object({ user_id: z.string().uuid() });
export const UniqueVotersResponseSchema = z.object({
  unique_voters: z.array(UniqueVoterSchema),
});

export const VotesCountResponseSchema = z.object({
  votes_count: z.number().int().nonnegative(),
});

export const VoteOnDuelRequestSchema = z.object({
  duel_id: z.string().uuid(),
  selected_player_id: z.string().uuid().nullable(),
  confirm_kind: z.enum(['earlyBonus', 'final', 'finalWithEarlyBonus']).optional(),
});

export const VoteOnDuelResponseSchema = z.object({
  success: z.boolean(),
  data: DuelVoteSchema
    .extend({
      revote: z
        .object({
          state: z.enum(['initial', 'changed', 'unchanged']),
          pick_change_count_after: z.number().int().nonnegative(),
          changes_left: z.number().int().nonnegative(),
          seconds_to_deadline: z.number().int(),
          gw_id: z.string().uuid(),
        })
        .optional(),
    })
    .optional()
    .nullable(),
  message: z.string().optional(),
});

export type DuelVoteDto = z.infer<typeof DuelVoteSchema>;
export type DuelVotesListResponse = z.infer<typeof DuelVotesListResponseSchema>;
export type UniqueVoter = z.infer<typeof UniqueVoterSchema>;
export type UniqueVotersResponse = z.infer<typeof UniqueVotersResponseSchema>;
export type VotesCountResponse = z.infer<typeof VotesCountResponseSchema>;
export type VoteOnDuelRequest = z.infer<typeof VoteOnDuelRequestSchema>;
export type VoteOnDuelResponse = z.infer<typeof VoteOnDuelResponseSchema>;

